import { COMPONENT_KINDS, FEEDER_KINDS } from '../types/SimpleSupplyPoint';
import { SimpleElectricalService } from '../modules/panel';
 import { MCB, RCD, CABLES } from '../electrical/Malzeme';
import { Rectangle, Text, PolyLine } from '../modules/documents';
import { FLOOR_TYPES, getRepresentedFloors } from '../redux/floorPlanSlice';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from './getActiveFloorPlanId';

/**
 * Simple SupplyPoint için document transform functions
 * Legacy transformFunctions.js'ye alternatif, çok daha basit yapı
 */

/**
 * Kat adından otomatik panel adı türet. Normal kat panelleri "{N}-NKT"
 * formatına otomatik dönüşmeli ki YC'de her katın paneli farklı isimle görünsün
 * (sanal kopyalar varsayılan olarak orijinal panelin adını taşıyor → aynı isimde
 * çakışma oluyordu).
 *
 * Floor types: NORMAL, BASEMENT, ROOF, GROUND vb. Şu an sadece NORMAL için
 * otomatik isim üretiliyor; diğerleri null döndürür (orijinal kullanıcı adı
 * korunur).
 */
function autoFloorPanelName(floorName) {
  if (!floorName) return null;
  // "1.Normal Kat", "1. Normal Kat", "10.Normal Kat" → "1-NKT", "10-NKT"
  const normal = floorName.match(/^(-?\d+)\.\s*Normal\s*Kat/i);
  if (normal) return `${normal[1]}-NKT`;
  // Bodrum/Çatı vb. ileride eklenebilir.
  return null;
}

/**
 * Kat kopyalama: floorCount > 1 olan kat planlarındaki panelleri sanal olarak çoğaltır.
 * Redux store'a dokunmaz, pure transform fonksiyonu.
 * @param {Object} simpleSupply - SimpleSupplyPoint
 * @param {Array} floorPlans - Sıralı kat planları (selectSortedFloorPlans)
 * @returns {Object} Genişletilmiş simpleSupply (components + feeders)
 */
export function expandSimpleSupplyForFloorCopies(simpleSupply, floorPlans) {
  if (!simpleSupply?.components || !floorPlans) return simpleSupply;

  const documentBounds = getGlobalDocumentBounds();
  const expandedComponents = [...simpleSupply.components];
  const expandedFeeders = [...simpleSupply.feeders];

  floorPlans.forEach(plan => {
    const floorCount = plan.floorCount || 1;
    if (floorCount <= 1) {
      return;
    }

    // Bu plana ait tüm katları al
    const representedFloors = getRepresentedFloors(plan);
    if (representedFloors.length <= 1) {
      return;
    }

    // Orijinal kat = representedFloors[0], kopyalanan katlar = [1..]
    const templateFloor = representedFloors[0];

    // Bu katta bulunan panelleri bul (pozisyon bazlı veya floorName bazlı)
    const templatePanels = simpleSupply.components.filter(c => {
      if (c.kind !== COMPONENT_KINDS.DIST_PANEL && c.kind !== COMPONENT_KINDS.PANEL) return false;
      // Pozisyon bazlı: panelin documentBounds içinde hangi kat planına düştüğüne bak
      if (c.position && documentBounds?.length > 0) {
        const fpId = getActiveFloorPlanId(c.position, documentBounds);
        return fpId === plan.id;
      }
      // Fallback: floorName eşlemesi
      const normalized = c.metadata?.floorName?.replace(/\.\s+/g, '.') || "";
      return normalized === templateFloor || c.metadata?.floorName === templateFloor;
    });

    if (templatePanels.length === 0) return;

    // Orijinal paneli (representedFloors[0]) yeniden adlandır — Normal kat ise
    // "{N}-NKT". Mutate yerine expandedComponents'teki ilgili elemanı yeni
    // objeyle değiştiriyoruz (Redux state'i mutate etmemek için).
    const origFloorName = templateFloor;
    const origAutoName = autoFloorPanelName(origFloorName);
    if (origAutoName) {
      templatePanels.forEach(panel => {
        const idx = expandedComponents.findIndex(c => c.id === panel.id);
        if (idx >= 0) {
          expandedComponents[idx] = {
            ...panel,
            name: origAutoName,
            metadata: { ...panel.metadata, floorName: origFloorName },
          };
        }
      });
    }

    // Kopyalanan her kat için panelleri ve bağlı feeder/load'ları klonla
    for (let fi = 1; fi < representedFloors.length; fi++) {
      const targetFloor = representedFloors[fi];
      const cloneAutoName = autoFloorPanelName(targetFloor);

      templatePanels.forEach(panel => {
        const newPanelId = `${panel.id}__copy_${fi}`;
        // Panel klonu — Normal kat ise auto isim, değilse orijinal ad
        expandedComponents.push({
          ...panel,
          id: newPanelId,
          name: cloneAutoName || panel.name,
          metadata: { ...panel.metadata, floorName: targetFloor, _virtualCopy: true },
        });

        // Bu panele gelen feeder klonu (mainPanel → panel)
        const incomingFeeder = simpleSupply.feeders.find(f => f.to === panel.id);
        if (incomingFeeder) {
          expandedFeeders.push({
            ...incomingFeeder,
            id: `${incomingFeeder.id}__copy_${fi}`,
            to: newPanelId,
          });
        }

        // Bu panelin child load'larını klonla
        const childLoads = simpleSupply.components.filter(
          c => c.kind === COMPONENT_KINDS.LOAD && c.parentComponentId === panel.id
        );
        childLoads.forEach(load => {
          const newLoadId = `${load.id}__copy_${fi}`;
          expandedComponents.push({
            ...load,
            id: newLoadId,
            parentComponentId: newPanelId,
            metadata: { ...load.metadata, _virtualCopy: true },
          });

          // Load'a gelen feeder klonu (panel → load)
          const loadFeeder = simpleSupply.feeders.find(f => f.to === load.id);
          if (loadFeeder) {
            expandedFeeders.push({
              ...loadFeeder,
              id: `${loadFeeder.id}__copy_${fi}`,
              from: newPanelId,
              to: newLoadId,
            });
          }
        });
      });
    }
  });

  return {
    ...simpleSupply,
    components: expandedComponents,
    feeders: expandedFeeders,
  };
}

/**
 * Simple SupplyPoint'i Yükleme Cetveli verisi'ne çevirir
 * @param {SimpleSupplyPoint} simpleSupply - Simple supply point
 * @returns {Array} Yükleme cetveli data
 */
export function transformSimpleSupplyToYC_Data(simpleSupply) {

  if (!simpleSupply?.components || !Array.isArray(simpleSupply.components)) {
    console.warn("simpleSupply.components tanımsız veya dizi değil.");
    return { panels: [], adp: null };
  }

  // 1. Panel component'lerini bul (dist_panel türünde olanlar)
  const panelComponents = simpleSupply.components.filter(c =>
    c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL
  );

  // 2. Ana panel component'ini bul (meter_panel türünde olan)
  const mainPanelComponent = simpleSupply.components.find(c => c.kind === COMPONENT_KINDS.METER_PANEL);

  let panels = [];
  let daireTotalPower = 0, daireDemandFakt = "60-40", daireDemandPower = 0;

  // Panel'leri işle - YC tablosuna uygun format
  panels = panelComponents.map((panel, index) => {
    // Panel'e bağlı feeder'ı bul
    const incomingFeeder = simpleSupply.feeders?.find(f => f.to === panel.id);
    // Panel entries'lerinden breaker ve RCD bilgilerini al
    const mainBreakerEntry = incomingFeeder.protection.mcb//panel.entries?.find(e => e.kind === 'main_breaker');
    const rcdEntry = incomingFeeder.protection.rcd// panel.entries?.find(e => e.kind === 'protective_device' || e.kind === 'main_protective_device');
  
    // Feeder'dan kablo bilgilerini al (indexNo kullanarak CABLES'den çek)
    let cableTitle = "";
    let cableCurrentLimit = 0;
    let cableCurrentCarryType = "";

    if (incomingFeeder?.cable?.indexNo) {
      const cableObject = CABLES[incomingFeeder.cable.indexNo];
      if (cableObject) {
        cableTitle = cableObject.title || "";
        cableCurrentLimit = cableObject.airCurCap || cableObject.currentCap || 0;
        cableCurrentCarryType = incomingFeeder.cable.housing || cableObject.housing || "";
      }
    } else {
      // Fallback: eski yöntemle title ve currentCap al
      cableTitle = incomingFeeder?.cable?.title || "";
      cableCurrentLimit = incomingFeeder?.cable?.currentCap || 0;
      cableCurrentCarryType = incomingFeeder?.cable?.housing || "";
    }

    // Breaker ve RCD title'larını oluştur (ProjectSettings indexNo ile MCB/RCD objelerinden)
    const mcbIndexNo = mainBreakerEntry?.indexNo || 423; // Default from ProjectSettings
    const rcdIndexNo = rcdEntry?.indexNo || 3; // Default from ProjectSettings

    const mcbObject = MCB[mcbIndexNo] || MCB[44]; // Fallback 25A MCB
    const rcdObject = RCD[rcdIndexNo] || RCD[3]; // Fallback 30mA RCD

    const panelMainBreaker = mcbObject; // MCB objesinin kendisini kullan
    const rcdTitle = rcdObject; // RCD objesinin kendisini kullan

    // Panel'e bağlı load component'lerini bul (branch circuits)
    const connectedLoads = simpleSupply.components.filter(c =>
      c.kind === COMPONENT_KINDS.LOAD && c.parentComponentId === panel.id
    );

    // Purpose of use'a göre güç değerlerini topla
    if (panel.electrical?.purposeOfUse === "Mesken") {
      daireTotalPower += panel.electrical.installedPower || 0;
      daireDemandFakt = panel.electrical.demandFactor || "60-40";
      daireDemandPower += panel.electrical.demandPower || 0;
    }

    return {
      name: panel.name || `Panel ${index + 1}`,
      id: panel.id,
      installedPower: panel.electrical?.installedPower || 0,
      demandPower: panel.electrical?.demandPower || 0,
      demandFactor: panel.electrical?.demandFactor || "60-40",
      phase: "rst",
      phaseCount: panel.electrical?.phaseCount || 3,
      phases: { r: 1, s: 1, t: 1, rst: 3 },
      current: panel.electrical?.current?.toFixed(2) || "0.00",
      panelMainBreaker,
      rcd: rcdTitle,
      cableTitle,
      cableCurrentLimit,
      cableCurrentCarryType,
      meterBreaker: null, // Bu dist_panel için değil
      subscribeType: panel.electrical?.purposeOfUse || "Mesken",
      type: panel.kind, // 'dist_panel' or 'panel'
      floorName: panel.metadata?.floorName || "Zemin Kat",
      branchCircuits:  transformSimpleSupplyToBranchCircuits(simpleSupply, panel.id).branchies || []
    };
  });

  // Ana Dağıtım Panosu (ADP) bilgilerini hazırla
  let adp = null;
  if (mainPanelComponent) {
    // Ana panel'e bağlı feeder
    const mainIncomingFeeder = simpleSupply.feeders?.find(f => f.to === mainPanelComponent.id);
     // Ana panel entries'leri (ProjectSettings'ten gelen değerlerle)
   // const mainBreakerEntry = mainPanelComponent.entries?.find(e => e.kind === 'main_breaker');
    const mainBreakerEntry = mainIncomingFeeder.protection.mcb
        const mainRcdEntry = mainIncomingFeeder.protection.rcd

/*     const cableTitle = mainIncomingFeeder?.cable?.title || "";
    const cableCurrentLimit = mainIncomingFeeder?.cable?.currentCap || 0;
    const cableCurrentCarryType = mainIncomingFeeder?.cable?.housing || "";
 */
    let cableTitle = "";
    let cableCurrentLimit = 0;
    let cableCurrentCarryType = "";

    if (mainIncomingFeeder?.cable?.indexNo) {
      const cableObject = CABLES[mainIncomingFeeder.cable.indexNo];
      if (cableObject) {
        cableTitle = cableObject.title || "";
        // ADP (MainPanel) için TOPRAK akım taşıma kapasitesi kullanılır
        // (kullanıcı kuralı): kolon hattı toprak içinden döşeniyor.
        // Diğer panellerde ise havadaki kapasite kullanılır (line 234).
        cableCurrentLimit = cableObject.earthCurCap || cableObject.airCurCap || cableObject.currentCap || 0;
        cableCurrentCarryType = mainIncomingFeeder.cable.housing || cableObject.housing || "";
      }
    } else {
      // Fallback: eski yöntemle title ve currentCap al
      cableTitle = mainIncomingFeeder?.cable?.title || "";
      cableCurrentLimit = mainIncomingFeeder?.cable?.currentCap || 0;
      cableCurrentCarryType = mainIncomingFeeder?.cable?.housing || "";
    }
    // Ana panel breaker ve RCD'leri indexNo ile al (ProjectSettings'ten gelen değerler)
    const mainMcbIndexNo = mainBreakerEntry?.indexNo || 555; // Default from ProjectSettings mainPanel
    const mainRcdIndexNo = mainRcdEntry?.indexNo || 9; // Default from ProjectSettings mainPanel

    const mainMcbObject = MCB[mainMcbIndexNo] || MCB[65]; // Fallback 63A MCB
    const mainRcdObject = RCD[mainRcdIndexNo] || RCD[9]; // Fallback 300mA RCD

    const mainBreaker = mainMcbObject; // MCB objesinin kendisini kullan
    const mainRcd = mainRcdObject; // RCD objesinin kendisini kullan

    adp = {
      name: mainPanelComponent.name || "Ana Dağıtım Panosu",
      floorName: mainPanelComponent.metadata?.floorName || "Zemin Kat",
      rcd: mainRcd,
      breaker: mainBreaker,
      cableTitle,
      cableCurrentLimit,
      current: mainPanelComponent.electrical?.current?.toFixed(2) || "0.00",
      cableCurrentCarryType,

      powers: {
        phases: { r: 0, s: 0, t: 0, rst: 0 },
        daireTotalPower,
        musterekTotalPower: mainPanelComponent.electrical?.musterekTotalPower || 0,
        isyeriTotalPower: mainPanelComponent.electrical?.isyeriTotalPower || 0,
        asansorTotalPower: mainPanelComponent.electrical?.asansorTotalPower || 0,
        daireDemandFakt,
        musterekDemandFakt: mainPanelComponent.electrical?.musterekDemandFakt || 0,
        isyeriDemandFakt: mainPanelComponent.electrical?.isyeriDemandFakt || 0,
        asansorDemandFakt: mainPanelComponent.electrical?.asansorDemandFakt || 0,
        daireDemandPower,
        musterekDemandPower: mainPanelComponent.electrical?.musterekDemandPower || 0,
        isyeriDemandPower: mainPanelComponent.electrical?.isyeriDemandPower || 0,
        asansorDemandPower: mainPanelComponent.electrical?.asansorDemandPower || 0,
      },
      installedTotalPower: mainPanelComponent.electrical?.installedPower || 0,
      demandPower: mainPanelComponent.electrical?.demandPower || 0,
      demandFaktor: mainPanelComponent.electrical?.demandFactor ||0
    };
  }

  const result = { panels, adp };
  return result;
}

/**
 * Simple SupplyPoint'i Gerilim Düşümü verisi'ne çevirir
 * @param {SimpleSupplyPoint} simpleSupply - Simple supply point
 * @returns {Object} Gerilim düşümü document formatı
 */
export function transformSimpleSupplyToVoltageDropData(simpleSupply) {
  const components = simpleSupply.components || [];
  const feeders = simpleSupply.feeders || [];

  if (feeders.length === 0) {
     return null;
  }

  // Helper function to get component by id
  const getComponent = (componentId) => {
    return components.find(c => c.id === componentId);
  };

  // Helper function to get cable info from indexNo
  const getCableInfo = (feeder) => {
    if (feeder.cable?.indexNo) {
      const cableObject = CABLES[feeder.cable.indexNo];
      if (cableObject) {
        return {
          title: cableObject.title || `${cableObject.coreCount}x${cableObject.section}mm² ${cableObject.type}`,
          type: cableObject.type,
          material: cableObject.material,
          section: cableObject.section,
          length: feeder.cable.length || 0
        };
      }
    }
    return {
      title: feeder.cable?.title || "4x2.5mm² NYM",
      type: feeder.cable?.type || "NYM",
      material: feeder.cable?.material || "Cu",
      section: feeder.cable?.section || 2.5,
      length: feeder.cable?.length || 0
    };
  };

  // Find floor name from metadata
  const floorComponent = components.find(c => c.metadata?.floorName);
  const floorName = floorComponent?.metadata?.floorName || "Zemin Kat";

  // Find supply source name
  const supplyComponent = components.find(c => c.kind === COMPONENT_KINDS.SUPPLY);
  const startName = supplyComponent?.name || "Şebeke";
  const startNode = "BOX"; // Default node type

  // Calculate lengths (mesafeler)
  const lengths = {
    enOda_KabBac: 3, // Default horizontal distance to cable shaft
    kabBac_Daire: 4, // Default distance from cable shaft to apartment
    kabBac: 5        // Default vertical distance in cable shaft
  };

  // Build circuits array: [mainFeeder, panelFeeder, branch]
  const circuits = [];

  // ✅ circuit[0]: mainPanelFeeder (SUPPLY → METER_PANEL) - Ana Pano Feeder
  // Önce normal yoldan dene (from → to ilişkisi)
  let mainPanelFeeder = feeders.find(f => {
    const fromComp = getComponent(f.from);
    const toComp = getComponent(f.to);
    return fromComp?.kind === COMPONENT_KINDS.SUPPLY &&
           toComp?.kind === COMPONENT_KINDS.METER_PANEL;
  });

  // Bulunamazsa, kind='main_feeder' olan feeder'ı al (from undefined olabilir)
  if (!mainPanelFeeder) {
    mainPanelFeeder = feeders.find(f => f.kind === FEEDER_KINDS.MAIN_FEEDER);
   }

   if (mainPanelFeeder) {
    const toComponent = getComponent(mainPanelFeeder.to);
    const feederElectrical = SimpleElectricalService.calculateFeederElectrical(mainPanelFeeder.id, simpleSupply);
     if (feederElectrical && toComponent) {
      const cableInfo = getCableInfo(mainPanelFeeder);
       const voltage = mainPanelFeeder.electrical?.phaseCount === 3 ? 380 : 220;
      const phase = mainPanelFeeder.electrical?.phaseCount || 3;
      const power = feederElectrical.demandPower || 0;
      const crosSecArea = cableInfo.section;

      circuits.push({
        length: mainPanelFeeder.cable?.length || 10, // ✅ Feeder'dan uzunluk al, yoksa 10m
        voltage: voltage,
        power: power,
        crosSecArea: crosSecArea,
        phase: phase,
        name: "ADP", // ✅ Ana Dağıtım Panosu
        cable: cableInfo.title
      });
     }
  }

  // ✅ circuit[1]: panelFeeder (METER_PANEL → DIST_PANEL veya DIST_PANEL → PANEL) - 5m
  let panelFeeder = feeders.find(f => {
    const fromComp = getComponent(f.from);
    const toComp = getComponent(f.to);
    return (fromComp?.kind === COMPONENT_KINDS.METER_PANEL && toComp?.kind === COMPONENT_KINDS.DIST_PANEL) ||
           (fromComp?.kind === COMPONENT_KINDS.DIST_PANEL && toComp?.kind === COMPONENT_KINDS.PANEL);
  });

  // Bulunamazsa, kind='panel_feeder' olan feeder'ı al
  if (!panelFeeder) {
    panelFeeder = feeders.find(f => f.kind === FEEDER_KINDS.PANEL_FEEDER);
   }

 
  if (panelFeeder) {
    const toComponent = getComponent(panelFeeder.to);
    const feederElectrical = SimpleElectricalService.calculateFeederElectrical(panelFeeder.id, simpleSupply);
     if (feederElectrical && toComponent) {
      const cableInfo = getCableInfo(panelFeeder);
      const voltage = panelFeeder.electrical?.phaseCount === 3 ? 380 : 220;
      const phase = panelFeeder.electrical?.phaseCount || 3;
      const power = feederElectrical.demandPower || 0;
      const crosSecArea = cableInfo.section;

      circuits.push({
        length: panelFeeder.cable?.length || 5, // ✅ Feeder'dan uzunluk al, yoksa 5m
        voltage: voltage,
        power: power,
        crosSecArea: crosSecArea,
        phase: phase,
        name: toComponent.name || "ZKT", // ✅ Panel adı
        cable: cableInfo.title
      });
     }
  }

  // ✅ circuit[2]: branch (PANEL/DIST_PANEL → en büyük güçlü LOAD) - 7m
  // Panel'den çıkan load'ları bul ve en büyük güçlüyü seç
  const panelComponent = components.find(c =>
    c.kind === COMPONENT_KINDS.PANEL ||
    c.kind === COMPONENT_KINDS.DIST_PANEL ||
    c.kind === COMPONENT_KINDS.METER_PANEL  // MainPanel'den direkt çıkış olabilir
  );
 
  if (panelComponent) {
    // Branch circuit türlerini filtrele
    const branchKinds = [
      FEEDER_KINDS.BRANCH_CIRCUIT,
      FEEDER_KINDS.SOCKET_CIRCUIT,
      FEEDER_KINDS.LIGHTING_CIRCUIT,
      FEEDER_KINDS.MACHINE_CIRCUIT,
      FEEDER_KINDS.LOWCURRENT_CIRCUIT
    ];

    const branchFeeders = feeders.filter(f => {
      const toComp = getComponent(f.to);
      // Ya panel'den çıkıyor ve LOAD'a gidiyor, ya da kind branch türlerinden biri
      return (f.from === panelComponent.id && toComp?.kind === COMPONENT_KINDS.LOAD) ||
             branchKinds.includes(f.kind);
    });

 
    // En büyük güçlü branch'i bul
    let maxPowerBranch = null;
    let maxPower = 0;

    branchFeeders.forEach(branchFeeder => {
      const feederElectrical = SimpleElectricalService.calculateFeederElectrical(branchFeeder.id, simpleSupply);
      if (feederElectrical && feederElectrical.demandPower > maxPower) {
        maxPower = feederElectrical.demandPower;
        maxPowerBranch = branchFeeder;
      }
    });

 
    if (maxPowerBranch) {
      const toComponent = getComponent(maxPowerBranch.to);
      const feederElectrical = SimpleElectricalService.calculateFeederElectrical(maxPowerBranch.id, simpleSupply);
      if (feederElectrical && toComponent) {
        const cableInfo = getCableInfo(maxPowerBranch);
        const voltage = maxPowerBranch.electrical?.phaseCount === 3 ? 380 : 220;
        const phase = maxPowerBranch.electrical?.phaseCount || 1;
        const power = feederElectrical.demandPower || 0;
        const crosSecArea = cableInfo.section;

        circuits.push({
          length: maxPowerBranch.cable?.length || 7, // ✅ Feeder'dan uzunluk al, yoksa 7m
          voltage: voltage,
          power: power,
          crosSecArea: crosSecArea,
          phase: phase,
          name: toComponent.name || "ÇM", // ✅ Component'in gerçek adı
          cable: cableInfo.title
        });
       }
    }
  }

 
  if (circuits.length === 0) {
    console.warn('DEBUG VD - No circuits found!');
    return null;
  }

  return {
    floorName,
    startName,
    startNode,
    lengths,
    circuits
  };
}

/**
 * Simple SupplyPoint'i Branch Circuits verisi'ne çevirir (Kolon ve TekHat şemaları için)
 * @param {SimpleSupplyPoint} simpleSupply - Simple supply point
 * @returns {Object} {branchies, feeder, kolonDetails} formatında veri
 */
export function transformSimpleSupplyToBranchCircuits(simpleSupply,componentId) {
  const branchies = [];
  const components = simpleSupply.components || [];
  const feeders = simpleSupply.feeders || [];

  // ✅ componentId yoksa, DIST_PANEL veya PANEL tipinde component bul (YC_Data ile aynı mantık)
  if(!componentId){
    const panelComp = components.find(c =>
      c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL
    );
    componentId = panelComp?.id;
  }

  // Panel component'i bul
  const panelComponent = components.find(c => c.id === componentId);

  if (!panelComponent) {
    console.warn("⚠️ transformSimpleSupplyToBranchCircuits: Panel component bulunamadı", { componentId, components: components.map(c => ({ id: c.id, kind: c.kind })) });
    return { branchies: [], feeder: {}, kolonDetails: {} };
  }

  // Redux'taki LOAD component'lerini bul (panel'in child'ları)
  const loadComponentsFromRedux = components.filter(c =>
    c.kind === COMPONENT_KINDS.LOAD &&
    c.parentComponentId === componentId
  );

 
  // Branch circuit'ları Redux component'lerinden oluştur
  loadComponentsFromRedux.forEach((loadComponent, index) => {
    // Load component'e gelen feeder'ı bul
    const incomingFeeder = feeders.find(f =>
      f.targetId === loadComponent.id ||
      f.to === loadComponent.id
    );

    // ✅ incomingFeeder yoksa bu branch'i atla
    if (!incomingFeeder) {
      console.warn("⚠️ Load component için feeder bulunamadı:", loadComponent.id);
      return;
    }

    const branchBreaker = incomingFeeder.protection?.mcb
    // Component metadata'sından type belirle
    let type, sorti, description;
    const branchType = loadComponent.metadata?.branchType || loadComponent.metadata?.loadType;

    // ✅ Feeder kind'ına göre lowCurrent kontrolü
    if (incomingFeeder?.kind === FEEDER_KINDS.LOWCURRENT_CIRCUIT) {
      type = 'lowCurrent';
      sorti = 1;
      description = loadComponent.name || "Zayıf Akım";
    } else {
      switch (branchType) {
        case 'machine':
           if (loadComponent.name?.includes('Çamaşır')) {
            type = 'socketWash';
            sorti = 1;
            description = "Çamaşır Makinesi";
          } else if (loadComponent.name?.includes('Bulaşık')) {
            type = 'socketDish';
            sorti = 1;
            description = "Bulaşık Makinesi";
          } else if (loadComponent.name?.includes('Fırın')) {
            type = 'socketOven';
            sorti = 1;
            description = "Fırın";
          
          } else if (loadComponent.name?.includes('Kombi')) {
            type = 'socketCombi';
            sorti = 1;
            description = "Kombi";
          
          } else if (loadComponent.name?.includes('Klima')) {
            type = 'socketAirCond';
            sorti = 1;
            description = "Klima";
          
          } else if (loadComponent.name?.includes('Fan')) {
            type = 'socketFan';
            sorti = 1;
            description = "Fan";
          
          } else if (loadComponent.name?.includes('Motor')) {
            type = 'socketMotor';
            sorti = 1;
            description = "Motor";
          
          } else if (loadComponent.name?.includes('Hidrofor')) {
            type = 'socketHidrofor';
            sorti = 1;
            description = "Hidrofor";
          }
          break;
        case 'socket':
          type = 'socket';
          sorti = loadComponent.metadata?.socketCount || 1;
          description = "Priz Linyesi";
          break;
        case 'lighting':
        case 'light':
          type = 'light';
          // Sorti = aydınlatma linyesindeki nokta (armatür) sayısı. Armatür-bazlı
          // yüklerde fixtures.length öncelikli; yoksa (sadece-anahtar yük) switchCount'a düş.
          sorti = loadComponent.metadata?.fixtures?.length
            || loadComponent.metadata?.lightCount
            || loadComponent.metadata?.switchCount
            || 1;
          description = "Aydınlatma Linyesi";
          break;
        default:
          type = 'socket';
          sorti = 1;
          description = loadComponent.name || "Yük";
      }
    }

    // Breaker bilgisini feeder'dan al
    let breakerInfo = null;
    
        if (branchBreaker) {
        breakerInfo = {
          breaker1Curve: branchBreaker.curve || "B",
          current: branchBreaker.current || 16,
          shortCircuitCurCap: 3, // Tüm panel çıkış hatları için 3kA
          type: branchBreaker.type || "MCB",
          indexNo: branchBreaker.IndexNo
        };
      }
   

    // Fallback breaker info
    if (!breakerInfo) {
      breakerInfo = {
        breaker1Curve: "B",
        current: 16,
        shortCircuitCurCap: 3, // Tüm panel çıkış hatları için 3kA
        type: "MCB"
      };
    }

    // Cable bilgisini feeder'dan al
    let cableInfo = {};
    if (incomingFeeder?.cable?.indexNo) {
      const cableObject = CABLES[incomingFeeder.cable.indexNo];
      if (cableObject) {
        cableInfo = {
          type: cableObject.type,
          material: cableObject.material || 'copper',
          section: cableObject.section,
          length: incomingFeeder.cable.length || 0
        };
      }
    }

    // ✅ Redux component'teki güncel installedPower değerini kullan
    const totalPower = loadComponent.electrical?.installedPower ||
                       loadComponent.socketPower ||
                       loadComponent.power ||
                       0;

    // ✅ Feeder'dan titleNumber al (panel çıkış sıralaması için)
    const titleNumber = incomingFeeder.titleNumber ?? 999999;

    branchies.push({
      breaker: breakerInfo,
      cable: cableInfo,
      description,
      id: `branch-${index + 1}`,
      phaseCount: loadComponent.electrical?.phaseCount || 1,
      sorti: sorti,
      totalPower: totalPower, // Redux'tan güncel değer
      type: type,
      titleNumber, // ✅ Sıralama için titleNumber ekle
    });
  });

  // ✅ Branch'ları feeder titleNumber'a göre sırala (panel çıkış sıralaması)
 
  branchies.sort((a, b) => {
    const orderA = a.titleNumber ?? 999999;
    const orderB = b.titleNumber ?? 999999;
    return orderA - orderB;
  });

 
  let feederData = {};
  // Ana feeder bilgilerini al (MAIN_FEEDER)
  const panelFeeder = feeders.find(f => f.kind === FEEDER_KINDS.PANEL_FEEDER);
  const  panel = components.find(c => c.kind === COMPONENT_KINDS.DIST_PANEL);
   if (panelFeeder && panel) {
    // Main feeder cable bilgisini indexNo ile al
    let cableTitle = "4x6 mm² NYY";
    if (panelFeeder.cable?.indexNo) {
      const cableObject = CABLES[panelFeeder.cable.indexNo];
      if (cableObject) {
        cableTitle = cableObject.title || cableTitle;
      }
    }

    // Meter panel entries'lerinden breaker ve RCD al
    const mainBreakerEntry = panel.entries?.find(e => e.kind === 'main_breaker');
    const rcdEntry = panel.entries?.find(e => e.kind === 'protective_device');
    let breakerText1 = "C-4x25A";
    let breakerText2 = "10kA";
    let rcdText1 = "25A-4P/30mA";
    let rcdText2 = "(25-32)A";

    if (mainBreakerEntry?.indexNo) {
      const mcbObject = MCB[mainBreakerEntry.indexNo]; 
      if (mcbObject) {
        breakerText1 = `${mcbObject.curve || 'C'}-${mcbObject.polar}x${mcbObject.current}A`;
        breakerText2 = `${mcbObject.shortCircuitCurCap}kA`;
      }
    }

    if (rcdEntry?.indexNo) {
      const rcdObject = RCD[rcdEntry.indexNo];
      if (rcdObject) {
        rcdText1 = `${rcdObject.pole}-${rcdObject.current}A/${rcdObject.sensitivity}mA`;
      }
    }

    feederData = {
      type: "riser03",
      components: [
        {
          symbolCode: "CABLE",
          text: cableTitle
        },
        {
          symbolCode: "AOS1P",
          text1: breakerText1,
          text2: breakerText2
        },
        {
          symbolCode: "KAKR",
          text1: rcdText1,
          text2: "K.A.K.R.",
          text3: rcdText2
        }
      ],
      panelNames: [ panelComponent.name || "Panel" ], // Panel isimlerini simpleSupply'dan alabilir
      typeName: "TİP-3",
      installedPower: panel.electrical?.installedPower || 0,
      demandPower: panel.electrical?.demandPower || 0,
      panelType: "panel",
      placement: "bottom",
      outsY: 20,
      outsX: 75,
      textColor: 0xffffff,
      lineColor: 0x0000ff,
      symbolColor: 0xff0000
    };
 
  }

  const mainFeeder = feeders.find(f => f.kind === FEEDER_KINDS.MAIN_FEEDER);
  const meterPanel = components.find(c => c.kind === COMPONENT_KINDS.METER_PANEL);
 
  let kolonDetails = {};

  if (mainFeeder && meterPanel) {
    // Main feeder cable bilgisini indexNo ile al
    let cableTitle = "4x6 mm² NYY";
    if (mainFeeder.cable?.indexNo) {
      const cableObject = CABLES[mainFeeder.cable.indexNo];
      if (cableObject) {
        cableTitle = cableObject.title || cableTitle;
      }
    }

    // Meter panel entries'lerinden breaker ve RCD al
    //const mainBreakerEntry = meterPanel.entries?.find(e => e.kind === 'main_breaker');
    const mainBreakerEntry =  mainFeeder.protection.mcb

    const rcdEntry = meterPanel.entries?.find(e => e.kind === 'main_protective_device');
     let breakerText1 = "C-4x25A";
    let breakerText2 = "10kA";
    let rcdText1 = "25A-4P/30mA";
    let rcdText2 = "(25-32)A";

    if (mainBreakerEntry?.indexNo) {
      const mcbObject = MCB[mainBreakerEntry.indexNo]; 
      if (mcbObject) {
        breakerText1 = `${mcbObject.curve || 'C'}-${mcbObject.polar}x${mcbObject.current}A`;
        breakerText2 = `${mcbObject.shortCircuitCurCap}kA`;
      }
    }

    if (rcdEntry?.indexNo) {
      const rcdObject = RCD[rcdEntry.indexNo];
      if (rcdObject) {
        rcdText1 = `${rcdObject.pole}-${rcdObject.current}A/${rcdObject.sensitivity}mA`;
      }
    }

   

    kolonDetails = {
      cable: cableTitle,
      pipe: "ɸ=100 Pe (kangal) boru",
      breaker: {
        text1: breakerText1,
        text2: breakerText2
      },
      rcd: {
        text1: rcdText1,
        text2: rcdText2,
        text3: "K.A.K.R."
      },
      grounding: [
        "Pot. deng. iletkeni en az 16 mm² NYAF",
        "Bina Giriş Dağıtım Barası"
      ],
      panelName:meterPanel.name
    };
  }

  const result = { branchies, feeder: feederData, kolonDetails };
   return result;
}

/**
 * Legacy SupplyPoint'i Simple SupplyPoint'e çevirir
 * @param {object} legacySupply - Mevcut karmaşık supply point
 * @returns {SimpleSupplyPoint} Simple supply point
 */
export function transformLegacyToSimpleSupplyPoint(legacySupply) {
 
  const components = [];
  const feeders = [];

  // 1. Housing'leri component'lere çevir
  legacySupply.housings?.forEach(housing => {
    const componentKind = determineComponentKind(housing);

    components.push({
      id: housing.id,
      kind: componentKind,
      name: housing.name,
      electrical: housing.electrical || {},
      incomingFeeders: [], // Sonra doldurulacak
      outgoingFeeders: [], // Sonra doldurulacak
      metadata: {
        type: housing.type,
        feederType: housing.feederType,
        components: housing.components
      }
    });
  });

  // 2. Connection'ları feeder'lara çevir
  legacySupply.connections?.forEach(connection => {
    if (connection.feederType && connection.fromHousing && connection.toHousing) {
      feeders.push({
        id: connection.id,
        from: connection.fromHousing,
        to: connection.toHousing,
        type: connection.feederType,
        kind: determineFeederKind(connection),
        power: {
          installedPower: 0, // Hesaplanacak
          demandPower: 0,    // Hesaplanacak
          current: 0         // Hesaplanacak
        },
        phaseCount: 3, // Default
        cable: connection.cable || {},
        protection: extractProtectionFromConnection(connection),
        metadata: {
          type: connection.type,
          masterNodeId: connection.masterNodeId
        }
      });
    }
  });

  // 3. Branch circuits'i ekle
  legacySupply.branchCircuits?.forEach(branch => {
    // Branch component
    components.push({
      id: branch.id,
      kind: COMPONENT_KINDS.BRANCH,
      name: branch.name || `Branch ${branch.id}`,
      electrical: {
        installedPower: branch.power || 0,
        demandPower: branch.power || 0,
        current: calculateCurrentFromPower(branch.power, branch.phaseCount)
      },
      incomingFeeders: [], // Housing'den gelecek
      outgoingFeeders: [],
      metadata: {
        branchType: branch.branchType,
        housing: branch.housing
      }
    });
  });

  // 4. Load'ları ekle
  legacySupply.loads?.forEach(load => {
    components.push({
      id: load.id,
      kind: COMPONENT_KINDS.LOAD,
      name: load.name || `Load ${load.id}`,
      electrical: {
        installedPower: load.power || 0,
        demandPower: load.power || 0,
        current: calculateCurrentFromPower(load.power, 1)
      },
      incomingFeeders: [], // Branch'den gelecek
      outgoingFeeders: [],
      metadata: {
        loadType: load.branchType,
        from: load.from
      }
    });
  });

  // 5. Relationship'leri güncelle
  updateComponentRelationships(components, feeders);

 
  return {
    feeders,
    components,
    metadata: {
      transformedFrom: "legacy",
      transformedAt: new Date().toISOString()
    }
  };
}

// Helper functions

function determineComponentKind(housing) {
  if (housing.feederType === "supply") return COMPONENT_KINDS.SUPPLY;
  if (housing.type === "meterPanel") return COMPONENT_KINDS.METER_PANEL;
  if (housing.type === "distributionPanel") return COMPONENT_KINDS.DIST_PANEL;
  return COMPONENT_KINDS.DIST_PANEL; // Default
}

function determineFeederKind(connection) {
  if (connection.feederType === "mf-03-a") return FEEDER_KINDS.MAIN_FEEDER;
  if (connection.feederType === "mf-03-b") return FEEDER_KINDS.SUB_FEEDER;
  return FEEDER_KINDS.BRANCH_CIRCUIT;
}

function extractProtectionFromConnection(connection) {
  // Legacy connection'dan protection bilgilerini çıkar
  // Bu implementation proje yapısına göre adapte edilmeli
  return {
    breaker1: null,
    breaker2: null
  };
}

function calculateCurrentFromPower(power, phaseCount = 3) {
  if (!power) return 0;
  const voltage = phaseCount === 3 ? 380 : 220;
  return power / voltage;
}

function calculateCableResistance(cable) {
  // Kablo direnci hesaplama (Ω/km)
  if (!cable) return 0.0175 / 1.5; // Default Cu 1.5mm²
  const resistivity = (cable.material === "Cu") ? 0.0175 : 0.028; // Cu: 0.0175, Al: 0.028
  const section = cable.section || 1.5; // Default 1.5mm²
  return resistivity / section; // Ω/km
}

function updateComponentRelationships(components, feeders) {
  // Feeder'lara göre component relationship'lerini güncelle
  feeders.forEach(feeder => {
    const fromComponent = components.find(c => c.id === feeder.from);
    const toComponent = components.find(c => c.id === feeder.to);

    if (fromComponent) {
      fromComponent.outgoingFeeders.push(feeder.id);
    }

    if (toComponent) {
      toComponent.incomingFeeders.push(feeder.id);
    }
  });
}
 function transformComponentsToBranchies(loadComponents,feeders) {
  if (!Array.isArray(loadComponents) || loadComponents.length === 0) {
    return [];
  }

  // ✅ Branch'ları oluştur (sıralama sonra yapılacak)
  const branchies = loadComponents.map((load, index) => {
    const branchId = `branch-${load.id}`;
    let type, sorti, description;
    const branchFeeder = feeders.find(f => f.to === load.id);

    // ✅ Feeder bulunamadıysa veya protection bilgisi eksikse bu load'u atla
    if (!branchFeeder || !branchFeeder.protection) {
      return null;
    }

    // ✅ Feeder'dan titleNumber al (panel çıkış sıralaması için)
    const titleNumber = branchFeeder.titleNumber ?? 999999;

    // Load type'ına göre branch özelliklerini belirle
    const loadType = load.metadata?.loadType || 'general';
    const installedPower = load.electrical?.installedPower || 0;

    switch (loadType) {
      case 'machine':
        const machineType = load.metadata?.machineType || load.name || 'Makine';
        if (machineType.includes('Çamaşır') || machineType.includes('Washing')) {
          type = 'socketWash';
          sorti = 1;
          description = "Çamaşır Makinesi";
        } else if (machineType.includes('Bulaşık') || machineType.includes('Dishwasher')) {
          type = 'socketDish';
          sorti = 1;
          description = "Bulaşık Makinesi";
        } else if (machineType.includes('Fırın') || machineType.includes('Oven')) {
          type = 'socketOven';
          sorti = 1;
          description = "Fırın";
        } else {
          type = 'machine';
          sorti = 1;
          description = machineType;
        }
        break;

      case 'socket':
        type = 'socket';
        sorti = load.metadata?.socketCount || 1; // Kaç adet priz
        description = "Priz Linyesi";
        break;

      case 'lighting':
        type = 'light';
        sorti = load.metadata?.fixtures?.length || 1; // Kaç adet armatur
        description = "Aydınlatma Linyesi";
        break;

      default:
        type = 'general';
        sorti = 1;
        description = load.name || "Genel Yük";
    }

    // IndexNo kullanarak MCB objesini çek
    const breakerIndexNo = load.metadata?.breakerIndexNo || 8; // Default 16A MCB (MCB[8])
    const breaker = MCB[breakerIndexNo] || MCB[8]; // MCB objesinin kendisini kullan

    return {
      id: branchId,
      type: type,
      sorti: sorti,
      totalPower: installedPower,
      breaker: branchFeeder.protection?.mcb || breaker,
      description,
      phaseCount: load.electrical?.phaseCount || 1,
      titleNumber, // ✅ Sıralama için titleNumber ekle
      cable: {
        type: load.metadata?.cableType || 'NYM',
        material: load.metadata?.cableMaterial || 'copper',
        section: load.metadata?.cableSection || 2.5,
        length: load.metadata?.cableLength || 15
      }
    };
  }).filter(Boolean); // ✅ null değerleri filtrele

  // ✅ Branch'ları feeder titleNumber'a göre sırala (panel çıkış sıralaması)
 
  branchies.sort((a, b) => {
    const orderA = a.titleNumber ?? 999999;
    const orderB = b.titleNumber ?? 999999;
    return orderA - orderB;
  });

 
  return branchies;
}
export function createMainFrameParts(frameWidth, frameHeight, offset) {
  const frameOptions = {
    color: 0xff0000,
    colorIndex: 1,
    layer: '0',
    shape: true,
    hasContinuousLinetypePattern: false
  };

  // Ana çerçeve vertices
  const vertices = [
    { x: -offset, y: 0, z: 0 },
    { x: frameWidth, y: 0, z: 0 },
    { x: frameWidth, y: -frameHeight, z: 0 },
    { x: -offset, y: -frameHeight, z: 0 },
    { x: -offset, y: 0, z: 0 } // Close the polyline
  ];

  // PolyLine utility kullanarak DXF entity oluştur
  return [PolyLine(-offset, 0, vertices, frameOptions)];
}

export function createDocumentHeaderParts(
  title,
  width,
  height,
  headerHeight,
  { drawFrame = true } = {}
) {
  const result = [];

  // Default options for DXF entities
  const frameOptions = {
    color: 0x666666,
    colorIndex: 8,
    layer: '0',
    shape: true,
    hasContinuousLinetypePattern: false
  };

  const headerOptions = {
    color: 0x4287f5,
    colorIndex: 5,
    layer: '0',
    shape: true,
    hasContinuousLinetypePattern: false
  };

  const safeWidth = Number.isFinite(width) && width > 0 ? width : 1;
  const safeHeight = Number.isFinite(headerHeight) && headerHeight > 0 ? headerHeight : 1;
  const safeFrameHeight = Number.isFinite(height) && height > 0 ? height : 1;

  // 1. Document frame - using Rectangle utility
  if (drawFrame) {
    result.push(Rectangle(0, 0, safeWidth, -safeFrameHeight, frameOptions));
  }

  // 2. Header background - using Rectangle utility
  result.push(Rectangle(0, 0, safeWidth, -safeHeight, headerOptions));

  // 3. Title text - using Text utility
  if (title && title.trim() !== '') {
    const textObj = {
      text: title,
      textHeight: Math.min(safeHeight * 0.6, 20),
      handle: `header_text_${Date.now()}`
    };

    result.push(Text(
      safeWidth / 2,
      -safeHeight / 2,
      textObj,
      0,
      frameOptions
    ));
  }

  return result;
}

/**
 * SimpleSupply + floorPlans'tan doğrudan kolonConfig ve tumKatlar üretir.
 * generateKolonSemaMultiMeter'ın beklediği formatta.
 *
 * @param {Object} simpleSupply - Redux simpleSupply state
 * @param {Array}  floorPlans   - Sıralı kat planları (selectSortedFloorPlans)
 * @returns {{ kolonConfig: Object, tumKatlar: string[] }}
 */
export function transformSimpleSupplyToKolonConfig(simpleSupply, floorPlans, panelConfig = null) {
  // Kat kopyalarına göre panelleri genişlet
  const expandedSupply = expandSimpleSupplyForFloorCopies(simpleSupply, floorPlans);

  // 1. ycData'yı genişletilmiş supply üzerinden üret
  const ycData = transformSimpleSupplyToYC_Data(expandedSupply);

  // 2. Panel floorName'lerini pozisyon bazlı override et
  //    (metadata.floorName yanlış/eksik olabilir — panelin fiziksel konumundan doğru katı bul)
  //    NOT: Sanal kopyaların (_virtualCopy) floorName'i expand fonksiyonunda zaten doğru set edildi, onları atla
  const documentBounds = getGlobalDocumentBounds();
  if (documentBounds && documentBounds.length > 0 && ycData?.panels) {
    ycData.panels.forEach(p => {
      const component = expandedSupply.components?.find(c => c.id === p.id);
      // Sanal kopyaların floorName'ini override etme (zaten doğru)
      if (component?.metadata?._virtualCopy) return;
      if (component?.position) {
        const floorPlanId = getActiveFloorPlanId(component.position, documentBounds);
        const floorPlan = floorPlans.find(fp => fp.id === floorPlanId);
        if (floorPlan) {
          const floors = getRepresentedFloors(floorPlan);
          if (floors.length > 0) {
            p.floorName = floors[0];
          }
        }
      }
    });
    // ADP (mainPanel) için de aynı kontrolü yap
    if (ycData.adp) {
      const adpComponent = expandedSupply.components?.find(
        c => c.kind === COMPONENT_KINDS.METER_PANEL
      );
      if (adpComponent?.position) {
        const floorPlanId = getActiveFloorPlanId(adpComponent.position, documentBounds);
        const floorPlan = floorPlans.find(fp => fp.id === floorPlanId);
        if (floorPlan) {
          const floors = getRepresentedFloors(floorPlan);
          if (floors.length > 0) {
            ycData.adp.floorName = floors[0];
          }
        }
      }
    }
  }

  // ADP installedTotalPower'ı genişletilmiş panellerden hesapla
  // (mainPanel'in kendi değeri sanal kopyaları içermiyor)
  if (ycData?.adp && ycData?.panels) {
    const expandedDaireTotalPower = ycData.adp.powers?.daireTotalPower || 0;
    const musterekTP = ycData.adp.powers?.musterekTotalPower || 0;
    const isyeriTP = ycData.adp.powers?.isyeriTotalPower || 0;
    const asansorTP = ycData.adp.powers?.asansorTotalPower || 0;
    ycData.adp.installedTotalPower = expandedDaireTotalPower + musterekTP + isyeriTP + asansorTP;
  }

  // --- Kat bilgilerini floorPlans'tan türet ---
  const basementCount = floorPlans
    .filter(p => p.type === FLOOR_TYPES.BASEMENT)
    .reduce((sum, p) => sum + (p.floorCount || 1), 0);
  const normalCount = floorPlans
    .filter(p => p.type === FLOOR_TYPES.NORMAL)
    .reduce((sum, p) => sum + (p.floorCount || 1), 0);
  const hasMezzanine = floorPlans.some(p => p.type === FLOOR_TYPES.MEZZANINE);
  const hasGallery = floorPlans.some(p => p.type === FLOOR_TYPES.GALLERY);
  const hasRoof = floorPlans.some(p => p.type === FLOOR_TYPES.ROOF);

  // --- tumKatlar oluştur: getRepresentedFloors kullanarak gerçek kat isimlerini al ---
  const tumKatlar = [];
  floorPlans.forEach(plan => {
    const floors = getRepresentedFloors(plan);
    floors.forEach(f => {
      if (!tumKatlar.includes(f)) tumKatlar.push(f);
    });
  });

  // --- Panel floorName → tumKatlar eşleme fonksiyonu ---
  const normalizeFloorName = (name) => name?.replace(/\.\s+/g, '.') || "";
  const floorNameToTumKatlar = {};
  tumKatlar.forEach(tk => {
    floorNameToTumKatlar[normalizeFloorName(tk)] = tk;
  });
  const mapFloorName = (panelFloorName) => {
    if (tumKatlar.includes(panelFloorName)) return panelFloorName;
    const normalized = normalizeFloorName(panelFloorName);
    return floorNameToTumKatlar[normalized] || panelFloorName;
  };

  // --- Panel verilerinden kat başına panel sayısı hesapla ---
  const panels = ycData?.panels || [];
  const hasGT = panels.some(p => p.subscribeType === "Müşterek");

  // --- Tali panelleri bul: ADP'den (mainPanel) beslenmeyen tüm paneller tali paneldir ---
  // ADP'den Ekle = sayaçlı panel, başka panelden Ekle = tali panel
  const mainPanelComponent = expandedSupply?.components?.find(c => c.kind === COMPONENT_KINDS.METER_PANEL);
  const mainPanelId = mainPanelComponent?.id;

  // Her panelin feeder kaynağını (parent) belirle
  const taliPanelIds = new Set(); // Tüm tali panel ID'leri
  const panelFeederSource = {}; // panelId → sourceId (feeder.from)
  const parentToPanels = {}; // parentPanelId → [childPanel, ...]
  if (expandedSupply?.components) {
    const feeders = expandedSupply.feeders || [];
    expandedSupply.components
      .filter(c =>
        (c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL) &&
        c.electrical?.purposeOfUse !== 'Müşterek' // GT panelinin kendisi hariç
      )
      .forEach(panel => {
        // 1. Feeder'dan parent bul (hem f.to hem f.targetId kontrol et)
        const incomingFeeder = feeders.find(f =>
          f.to === panel.id || f.targetId === panel.id
        );
        // 2. Fallback: parentComponentId (feeder yoksa bile parent bilgisi mevcut)
        const parentId = incomingFeeder?.from || panel.parentComponentId;
        if (!parentId) return; // Ne feeder ne parent varsa atla

        panelFeederSource[panel.id] = parentId;

        // Parent, mainPanel değilse → tali panel
        if (mainPanelId && parentId !== mainPanelId) {
          taliPanelIds.add(panel.id);
          if (!parentToPanels[parentId]) parentToPanels[parentId] = [];
          parentToPanels[parentId].push({ panel, feeder: incomingFeeder });
        }
      });
  }

  // --- GT tali panellerini oluştur ---
  const gtTaliPaneller = [];
  if (hasGT) {
    const gtPanel = expandedSupply?.components?.find(c =>
      (c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL) &&
      c.electrical?.purposeOfUse === 'Müşterek'
    );
    if (gtPanel && parentToPanels[gtPanel.id]) {
      parentToPanels[gtPanel.id].forEach(({ panel: childPanel, feeder }) => {
        // ycData.panels'ten pozisyon-düzeltilmiş floorName'i al
        const ycPanel = panels.find(p => p.id === childPanel.id);
        const floorName = mapFloorName(
          ycPanel?.floorName || childPanel.metadata?.floorName || "Zemin Kat"
        );
        // Ana kesici bilgisi (feeder protection MCB)
        const mcbEntry = feeder.protection?.mcb;
        const mcbObj = mcbEntry?.indexNo ? (MCB[mcbEntry.indexNo] || null) : null;
        // Sığınak paneli tespiti: adında veya purposeOfUse'unda "Sığınak" veya "S.T." geçiyorsa
        const isSiginakPanel = (p) => {
          const name = (p.name || '').toLowerCase();
          const purpose = (p.electrical?.purposeOfUse || '').toLowerCase();
          return name.includes('sığınak') || name.includes('siginak') || name.includes('s.t.')
            || purpose.includes('sığınak') || purpose.includes('siginak')
            || name.includes('sığınak panosu');
        };
        gtTaliPaneller.push({
          kat: floorName,
          key: childPanel.id,
          label: childPanel.name || 'Tali Panel',
          power: childPanel.electrical?.demandPower || childPanel.electrical?.installedPower || 0,
          breakerTitle: mcbObj?.title || '',
          isSiginak: isSiginakPanel(childPanel),
        });
      });
    }
  }

  const panelsPerFloor = {};
  panels.forEach(p => {
    // Müşterek (GT) panel ayrı çiziliyor, sayaç sayısına dahil etme
    if (p.subscribeType === 'Müşterek') return;
    // Tali paneller sayaç sayısına dahil etme (parent panelden besleniyorlar)
    if (taliPanelIds.has(p.id)) return;
    const floor = mapFloorName(p.floorName || "Zemin Kat");
    panelsPerFloor[floor] = (panelsPerFloor[floor] || 0) + 1;
  });
  const floorCounts = Object.values(panelsPerFloor);
  const katBasiDaire = floorCounts.length > 0 ? Math.max(...floorCounts) : 0;

  // --- MainPanel (ADP) katını enerji odası katı olarak belirle ---
  const mainPanelFloor = mapFloorName(ycData?.adp?.floorName || "Zemin Kat");
  const enerjiOdasiKati = tumKatlar.includes(mainPanelFloor) ? mainPanelFloor : "Zemin Kat";

  // --- katlar dizisi: her kat için panel detayları ---
  const katlar = tumKatlar.map((katName, idx) => {
    const katPanels = panels.filter(p =>
      mapFloorName(p.floorName || "Zemin Kat") === katName &&
      p.subscribeType !== 'Müşterek' &&
      !taliPanelIds.has(p.id)
    );
    return {
      name: katName,
      katNo: idx,
      katType: "normal",
      paneller: katPanels.map((p, pi) => ({
        name: p.name || `${katName}-${pi + 1}`,
        sayacKesici: p.panelMainBreaker?.indexNo || 423,
        panelKesici: p.panelMainBreaker?.indexNo || 46,
        iletken: 44,
        mesafe: 12,
        guc: p.demandPower || 0,
        faz: "R",
        aboneTipi: p.subscribeType || "mesken",
        kullanimAmaci: p.subscribeType || "mesken"
      }))
    };
  });

  // --- Ana kolon hattı ---
  const anaKolonHatti = {
    iletken: 44,
    kesici: 555,
  };

  // --- GT (Müşterek) aydınlatma çıkışlarını say ---
  let gtMerdivenCikisCount = 0;
  let gtAsansorCikisCount = 0;
  let gtMerdivenArmaturMax = 0;
  let gtAsansorArmaturMax = 0;
  // Çizim fonksiyonu formatı: { "0": { "1.Normal Kat": true, "2.Normal Kat": true }, "1": { ... } }
  const computedGtMerdivenKatlar = {};
  const computedGtAsansorKatlar = {};

  if (hasGT && expandedSupply?.components) {
    // GT panelini bul
    const gtPanel = expandedSupply.components.find(c =>
      (c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL) &&
      c.electrical?.purposeOfUse === 'Müşterek'
    );
    if (gtPanel) {
      // GT panelinin yüklerini bul
      const gtLoads = expandedSupply.components.filter(c =>
        c.kind === COMPONENT_KINDS.LOAD && c.parentComponentId === gtPanel.id
      );

      // Aynı lightingSubType'a sahip load'ları grupla (tek çıkış, birden fazla kat)
      // Her kat devam ettirmesinde ayrı load oluşuyor ama hepsi aynı çıkışa ait
      const merdivenFloors = {}; // tüm merdiven aydınlatma katlarını birleştir
      const asansorFloors = {};
      let hasMerdivenCikis = false;
      let hasAsansorCikis = false;

      gtLoads.forEach(load => {
        const subType = load.metadata?.lightingSubType;
        const fixtureCount = load.metadata?.fixtures?.length || 0;
        // Her fixture'ın gücünü topla (yoksa varsayılan 9W)
        const fixtures = load.metadata?.fixtures || [];
        const totalPower = fixtures.reduce((sum, f) => sum + (f.power || 9), 0);

        // Fixture başına ortalama güç (varsayılan 9W)
        const perFixturePower = fixtureCount > 0 ? Math.round(totalPower / fixtureCount) : 9;

        if (subType === 'Merdiven Aydınlatma') {
          hasMerdivenCikis = true;
          // Sadece armatür yerleştirilmiş katları ekle
          if (fixtureCount > 0) {
            const floorArm = load.metadata?.floorArmatures;
            if (floorArm) {
              Object.keys(floorArm).forEach(floorName => {
                const mapped = mapFloorName(floorName);
                const floorData = floorArm[floorName];
                const cnt = floorData?.count || fixtureCount;
                const pwr = floorData?.power || (cnt * perFixturePower);
                const prev = merdivenFloors[mapped];
                merdivenFloors[mapped] = {
                  count: (prev?.count || 0) + cnt,
                  power: (prev?.power || 0) + pwr,
                };
              });
            }
            if (fixtureCount > gtMerdivenArmaturMax) gtMerdivenArmaturMax = fixtureCount;
          }
        } else if (subType === 'Asansör Aydınlatma') {
          hasAsansorCikis = true;
          // Sadece armatür yerleştirilmiş katları ekle
          if (fixtureCount > 0) {
            const floorArm = load.metadata?.floorArmatures;
            if (floorArm) {
              Object.keys(floorArm).forEach(floorName => {
                const mapped = mapFloorName(floorName);
                const floorData = floorArm[floorName];
                const cnt = floorData?.count || fixtureCount;
                const pwr = floorData?.power || (cnt * perFixturePower);
                const prev = asansorFloors[mapped];
                asansorFloors[mapped] = {
                  count: (prev?.count || 0) + cnt,
                  power: (prev?.power || 0) + pwr,
                };
              });
            }
            if (fixtureCount > gtAsansorArmaturMax) gtAsansorArmaturMax = fixtureCount;
          }
        }
      });

      // Çizim fonksiyonu formatı: { "0": { "katAdı": true } }
      if (hasMerdivenCikis) {
        gtMerdivenCikisCount = 1; // Tek merdiven aydınlatma çıkışı
        computedGtMerdivenKatlar["0"] = merdivenFloors;
      }
      if (hasAsansorCikis) {
        gtAsansorCikisCount = 1; // Tek asansör aydınlatma çıkışı
        computedGtAsansorKatlar["0"] = asansorFloors;
      }
    }
  }

  const kolonConfig = {
    hasPanelData: panels.length > 0,
    hasGT,
    normalKat: String(normalCount),
    bodrumKat: String(basementCount),
    asmaKat: hasMezzanine,
    galeriKat: hasGallery,
    catiKat: hasRoof,
    girisKati: "Zemin Kat",
    enerjiOdasiKati,
    katBasiDaire: String(katBasiDaire),
    panelsPerFloor,
    katlar,
    anaKolonHatti,
    anaHatFaz: "3 Faz",
    daireFaz: "1 Faz",
    asansor: false,
    asansorGucu: "15",
    musterek: hasGT,
    musterekGucu: "5",
    hidrofor: false,
    topraklamaMetalSu: false,
    topraklamaMetalGaz: false,
    topraklamaAsansorRay: true,
    topraklamaKabloMerdiven: false,
    tmsSimetrik: false,
    tmsTroidBobin: false,
    tmsAcilAcma: false,
    anaKolonFazi: "R",
    gtMerdivenCikis: String(gtMerdivenCikisCount),
    gtAsansorCikis: String(gtAsansorCikisCount),
    gtMerdivenArmatur: String(gtMerdivenArmaturMax || 4),
    gtAsansorArmatur: String(gtAsansorArmaturMax || 4),
    gtMerdivenKatlar: computedGtMerdivenKatlar,
    gtAsansorKatlar: computedGtAsansorKatlar,
    gtConfig: {},
    gtTaliPaneller,
    siginakJenerator: panelConfig?.SP?.jenerator || false,
  };

  return { kolonConfig, tumKatlar };
}

/**
 * Kablo Bacası Hesabı için veri dönüşümü
 * K (kat sayısı) ve A (kat başı daire) projeden otomatik türetilir,
 * diğer parametreler metadata'dan veya varsayılanlardan alınır.
 * @param {Object} simpleSupply - SimpleSupplyPoint
 * @param {Array} floorPlans - Sıralı kat planları
 * @returns {Object} { A, K, Rt, Rtv, Rk, TIP, d, L }
 */
export function transformSimpleSupplyToKabloBacasiData(simpleSupply, floorPlans) {
  // K = toplam kat sayısı (floorPlans'tan, floorCount dahil)
  const K = Array.isArray(floorPlans)
    ? floorPlans.reduce((sum, p) => sum + (p.floorCount || 1), 0)
    : 5;

  // A = kat başı ortalama bağımsız bölüm sayısı
  const panelCount = simpleSupply?.components?.filter(c =>
    c.kind === COMPONENT_KINDS.DIST_PANEL || c.kind === COMPONENT_KINDS.PANEL
  ).length || 0;
  const normalFloors = Array.isArray(floorPlans)
    ? floorPlans.filter(p => p.type === FLOOR_TYPES.NORMAL)
        .reduce((s, p) => s + (p.floorCount || 1), 0) || 1
    : 1;
  const A = panelCount > 0 ? Math.round(panelCount / normalFloors) : 1;

  // Kullanıcı override'ları metadata'dan
  const cableShaftMeta = simpleSupply?.metadata?.cableShaft || {};

  return {
    A: cableShaftMeta.A || A,
    K: cableShaftMeta.K || K,
    Rt: cableShaftMeta.Rt || 0.5,
    Rtv: cableShaftMeta.Rtv || 0.6,
    Rk: cableShaftMeta.Rk || 1.8,
    TIP: cableShaftMeta.TIP || 4,
    d: cableShaftMeta.d || 0.15,
    L: cableShaftMeta.L || 150,
  };
}

